/**
 * Security section — change password + active device sessions. Shared by all
 * user types. Password rules mirror the backend validator (min 8, upper,
 * lower, digit, special).
 */

import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { ProfileCard, PrimaryButton, Pill } from './ProfilePrimitives';
import SessionsList from './SessionsList';

const BORDER = '#ddd6c6';
const TEXT = '#08111f';
const MUTED = '#7a7060';

const STRONG = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;

function PasswordInput({ label, value, onChange }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[1px] font-semibold mb-[5px]" style={{ color: MUTED }}>
        {label}
      </div>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border text-[13px] bg-white outline-none focus:border-[#145f5f]"
        style={{ borderColor: BORDER, color: TEXT }}
        autoComplete="off"
      />
    </div>
  );
}

export default function SecuritySection() {
  const { changePassword } = useAuth();
  const { showToast } = useUI();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!current) return setError('Enter your current password.');
    if (!STRONG.test(next)) {
      return setError('New password needs 8+ chars with upper, lower, number, and special character.');
    }
    if (next !== confirm) return setError('New password and confirmation do not match.');
    if (next === current) return setError('New password must differ from the current one.');

    setSaving(true);
    try {
      const res = await changePassword(current, next);
      if (res?.success) {
        showToast('Password changed', 'success');
        setCurrent(''); setNext(''); setConfirm('');
      } else {
        setError(res?.message || 'Could not change password.');
      }
    } catch (err) {
      setError(err?.message || 'Could not change password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <ProfileCard title="Password" subtitle="Use a strong, unique password." icon="🔑">
        <form onSubmit={submit}>
          {error && (
            <div className="mb-4 px-3 py-2 rounded-lg text-[12px]" style={{ background: '#f2e9e6', color: '#9a564a' }}>
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <PasswordInput label="Current password" value={current} onChange={setCurrent} />
            <PasswordInput label="New password" value={next} onChange={setNext} />
            <PasswordInput label="Confirm new" value={confirm} onChange={setConfirm} />
          </div>
          <div className="mt-4">
            <PrimaryButton type="submit" disabled={saving}>
              {saving ? 'Updating…' : 'Update password'}
            </PrimaryButton>
          </div>
        </form>
      </ProfileCard>

      <ProfileCard
        title="Two-factor authentication"
        subtitle="A second verification step on every password sign-in."
        icon="🔐"
        action={<Pill tone="green">Active</Pill>}
      >
        <p className="m-0 text-[12.5px] leading-relaxed" style={{ color: TEXT }}>
          Two-factor authentication is always on for this account. Every time you sign in with your
          password, we email a 6-digit code (with a one-tap sign-in link) to your registered address —
          your password alone is never enough to access the account.
        </p>
        <p className="m-0 mt-2 text-[11px]" style={{ color: MUTED }}>
          Codes expire after 5 minutes. If you stop receiving them, check your spam folder or contact support.
        </p>
      </ProfileCard>

      <ProfileCard title="Devices & Sessions" subtitle="Where you're currently signed in." icon="🖥️">
        <SessionsList />
      </ProfileCard>
    </div>
  );
}
